/**
 * Token-bound neutralization mode product grid.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographDataTableColumn,
  MonographDataTableProps,
  MonographModeProductCell,
  MonographModeProductGenerator,
  MonographModeProductGridProps,
  MonographModeProductItem,
  MonographModeProductMode,
  MonographPanelTokens,
} from "./MonographPanelPrimitiveTypes.js";

interface ProductGridFactoryInput {
  readonly tokens: MonographPanelTokens;
  readonly DataTable: <TData>(props: MonographDataTableProps<TData>) => React.JSX.Element;
}

interface CellViewProps {
  readonly cell: MonographModeProductCell;
  readonly emptyLabel: React.ReactNode;
  readonly generator: MonographModeProductGenerator;
  readonly items: readonly MonographModeProductItem[];
  readonly mode: MonographModeProductMode;
  readonly onCellHover?: MonographModeProductGridProps["onCellHover"];
  readonly onCellLeave?: MonographModeProductGridProps["onCellLeave"];
  readonly onCellSelect?: MonographModeProductGridProps["onCellSelect"];
  readonly selected: boolean;
  readonly tokens: MonographPanelTokens;
}

function cellKey(generatorId: string, modeId: string): string {
  return `${generatorId}::${modeId}`;
}

function itemsByCell(
  items: readonly MonographModeProductItem[],
): ReadonlyMap<string, readonly MonographModeProductItem[]> {
  const grouped = new Map<string, MonographModeProductItem[]>();
  for (const item of items) {
    const key = cellKey(item.generatorId, item.modeId);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(item);
    else grouped.set(key, [item]);
  }
  return grouped;
}

function itemColor(
  item: MonographModeProductItem,
  mode: MonographModeProductMode,
  generator: MonographModeProductGenerator,
  tokens: MonographPanelTokens,
): string {
  return item.color ?? mode.color ?? generator.color ?? tokens.accent;
}

function ModeProductCell({
  cell,
  emptyLabel,
  generator,
  items,
  mode,
  onCellHover,
  onCellLeave,
  onCellSelect,
  selected,
  tokens,
}: CellViewProps): React.JSX.Element {
  const filled = items.length > 0;
  const firstItem = items[0];
  const color = firstItem
    ? itemColor(firstItem, mode, generator, tokens)
    : (mode.color ?? tokens.muted);
  const commonStyle: React.CSSProperties = {
    alignItems: "stretch",
    background: filled
      ? selected
        ? colorWithAlpha(color, "22")
        : colorWithAlpha(color, "10")
      : "rgba(7,6,13,0.50)",
    border: filled
      ? `1px solid ${colorWithAlpha(color, selected ? "66" : "44")}`
      : `1px dashed ${tokens.border}`,
    borderRadius: 10,
    boxShadow: selected && filled ? `0 0 16px ${colorWithAlpha(color, "33")}` : "none",
    color: filled ? color : tokens.muted,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minHeight: 44,
    padding: "9px 10px",
    textAlign: "left",
    transition: "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
    width: "100%",
  };
  const eventHandlers = {
    onBlur: onCellLeave,
    onFocus: (): void => {
      onCellHover?.(cell, items);
    },
    onMouseEnter: (): void => {
      onCellHover?.(cell, items);
    },
    onMouseLeave: onCellLeave,
  };

  const content = filled ? (
    items.map((item) => {
      const tone = itemColor(item, mode, generator, tokens);
      return (
        <span
          key={item.id}
          title={item.title}
          style={{
            alignItems: "center",
            background: colorWithAlpha(tone, item.accent ? "22" : "12"),
            border: `1px solid ${colorWithAlpha(tone, item.accent ? "66" : "44")}`,
            borderRadius: 8,
            color: tone,
            display: "inline-flex",
            flexWrap: "wrap",
            fontFamily: tokens.mono,
            fontSize: 11,
            fontWeight: 800,
            gap: 6,
            justifyContent: "space-between",
            lineHeight: 1.35,
            minHeight: 26,
            padding: "5px 7px",
            textShadow: item.accent ? `0 0 8px ${colorWithAlpha(tone, "66")}` : "none",
          }}
        >
          <span>{item.label}</span>
          {item.meta ? (
            <span style={{ color: tokens.muted, fontSize: 10, fontWeight: 700 }}>{item.meta}</span>
          ) : null}
        </span>
      );
    })
  ) : (
    <span
      style={{
        alignSelf: "center",
        fontFamily: tokens.mono,
        fontSize: 15,
        fontWeight: 800,
        lineHeight: "26px",
      }}
    >
      {emptyLabel}
    </span>
  );

  if (onCellSelect) {
    return (
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => {
          onCellSelect(cell, items);
        }}
        style={{
          ...commonStyle,
          cursor: "pointer",
        }}
        {...eventHandlers}
      >
        {content}
      </button>
    );
  }

  return (
    <div style={commonStyle} {...eventHandlers}>
      {content}
    </div>
  );
}

export function createMonographPanelProductGrid({ DataTable, tokens }: ProductGridFactoryInput): {
  readonly NeutralizationModeProductGrid: (
    props: MonographModeProductGridProps,
  ) => React.JSX.Element;
} {
  function NeutralizationModeProductGrid({
    generators,
    modes,
    items,
    ariaLabel,
    selectedCell,
    onCellSelect,
    onCellHover,
    onCellLeave,
    emptyLabel = "—",
    compact = true,
    minWidth = 640,
    className,
  }: MonographModeProductGridProps): React.JSX.Element {
    const cellItems = useMemo(() => itemsByCell(items), [items]);
    const columns = useMemo<MonographDataTableColumn<MonographModeProductGenerator>[]>(
      () => [
        {
          id: "generator",
          header: "Generator",
          cell: ({ row }): React.ReactNode => (
            <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span
                style={{
                  color: row.original.color ?? tokens.muted,
                  fontFamily: tokens.mono,
                  fontWeight: 800,
                }}
              >
                {row.original.id}
              </span>
              {row.original.label ? (
                <span style={{ color: tokens.text, fontSize: 12 }}>{row.original.label}</span>
              ) : null}
              {row.original.meta ? (
                <span style={{ color: tokens.muted, fontFamily: tokens.mono, fontSize: 10.5 }}>
                  {row.original.meta}
                </span>
              ) : null}
            </span>
          ),
        },
        ...modes.map<MonographDataTableColumn<MonographModeProductGenerator>>((mode) => ({
          id: mode.id,
          header: (): React.ReactNode => (
            <span style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center" }}>
              <span style={{ color: mode.color ?? tokens.muted }}>{mode.label ?? mode.id}</span>
              {mode.meta ? (
                <span style={{ color: tokens.muted, fontSize: 10, opacity: 0.75 }}>
                  {mode.meta}
                </span>
              ) : null}
            </span>
          ),
          cell: ({ row }): React.ReactNode => {
            const cell = { generatorId: row.original.id, modeId: mode.id };
            const groupedItems = cellItems.get(cellKey(cell.generatorId, cell.modeId)) ?? [];
            const selected =
              selectedCell?.generatorId === cell.generatorId && selectedCell.modeId === cell.modeId;
            return (
              <ModeProductCell
                cell={cell}
                emptyLabel={emptyLabel}
                generator={row.original}
                items={groupedItems}
                mode={mode}
                onCellHover={onCellHover}
                onCellLeave={onCellLeave}
                onCellSelect={onCellSelect}
                selected={selected}
                tokens={tokens}
              />
            );
          },
        })),
      ],
      [
        cellItems,
        emptyLabel,
        modes,
        onCellHover,
        onCellLeave,
        onCellSelect,
        selectedCell?.generatorId,
        selectedCell?.modeId,
        tokens,
      ],
    );

    return (
      <DataTable
        ariaLabel={ariaLabel}
        columns={columns}
        compact={compact}
        data={generators}
        getRowId={(row) => row.id}
        minWidth={minWidth}
        {...(className !== undefined ? { className } : {})}
      />
    );
  }

  return { NeutralizationModeProductGrid };
}
